import axios from "axios";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "";

import { buildApiUrl, modelMaxImageSize, type AiConfig } from "@/stores/use-config-store";
import { useUserStore } from "@/stores/use-user-store";
import { nanoid } from "nanoid";
import { dataUrlToFile } from "@/lib/image-utils";
import { buildImageReferencePromptText } from "@/lib/image-reference-prompt";
import { normalizeImageQuality, resolveImageRequestSize } from "@/lib/image-size";
import { imageToDataUrl } from "@/services/image-storage";
import type { ReferenceImage } from "@/types/image";

export type ChatCompletionMessage = {
    role: "system" | "user" | "assistant";
    content: string | Array<{ type: "text"; text: string } | { type: "image_url"; image_url: { url: string } }>;
};

type ImageApiResponse = {
    data?: Array<Record<string, unknown>>;
    error?: { message?: string };
    code?: number;
    msg?: string;
};

const IMAGE_OUTPUT_FORMAT = "png";

function resolveImageDataUrl(item: Record<string, unknown>) {
    if (typeof item.b64_json === "string" && item.b64_json) {
        return `data:image/png;base64,${item.b64_json}`;
    }
    if (typeof item.url === "string" && item.url) {
        return item.url;
    }
    return null;
}

function parseImagePayload(payload: ImageApiResponse) {
    if (typeof payload.code === "number" && payload.code !== 0) {
        throw new Error(payload.msg || "请求失败");
    }
    const images =
        payload.data
            ?.map(resolveImageDataUrl)
            .filter((value): value is string => Boolean(value))
            .map((dataUrl) => ({ id: nanoid(), dataUrl })) || [];

    if (images.length === 0) {
        throw new Error("接口没有返回图片");
    }

    return images;
}

function readAxiosError(error: unknown, fallback: string) {
    if (axios.isAxiosError<{ error?: { message?: string }; msg?: string; code?: number }>(error)) {
        const responseData = error.response?.data;
        return responseData?.msg || responseData?.error?.message || readStatusError(error.response?.status, fallback);
    }
    return error instanceof Error ? error.message : fallback;
}

function readStatusError(status: number | undefined, fallback: string) {
    if (status === 401 || status === 403) return "鉴权失败，请检查 API Key、套餐权限或模型权限";
    if (status === 429) return "请求被限流或额度不足，请稍后重试";
    return status ? `${fallback}：${status}` : fallback;
}

function parseStreamChunk(chunk: string, onDelta: (value: string) => void) {
    let deltaText = "";
    for (const eventBlock of chunk.split("\n\n")) {
        const data = eventBlock
            .split("\n")
            .find((line) => line.startsWith("data: "))
            ?.slice(6);
        if (!data || data === "[DONE]") continue;
        const delta = (JSON.parse(data) as { choices?: Array<{ delta?: { content?: string } }> }).choices?.[0]?.delta?.content || "";
        deltaText += delta;
    }
    if (deltaText) onDelta(deltaText);
}

function withSystemPrompt(config: AiConfig, prompt: string) {
    const systemPrompt = config.systemPrompt.trim();
    return systemPrompt ? `${systemPrompt}\n\n${prompt}` : prompt;
}

function aiApiUrl(config: AiConfig, path: string) {
    return config.channelMode === "remote" ? `${API_BASE}/api/v1${path}` : buildApiUrl(config.baseUrl, path);
}

function aiHeaders(config: AiConfig, contentType?: string) {
    const token = useUserStore.getState().token;
    return config.channelMode === "remote"
        ? {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(contentType ? { "Content-Type": contentType } : {}),
          }
        : {
              Authorization: `Bearer ${config.apiKey}`,
              ...(contentType ? { "Content-Type": contentType } : {}),
          };
}

function refreshRemoteUser(config: AiConfig) {
    if (config.channelMode === "remote") void useUserStore.getState().hydrateUser();
}

function withSystemMessage(config: AiConfig, messages: ChatCompletionMessage[]) {
    const systemPrompt = config.systemPrompt.trim();
    return systemPrompt ? [{ role: "system" as const, content: systemPrompt }, ...messages] : messages;
}

export async function requestGeneration(config: AiConfig, prompt: string) {
    const n = Math.max(1, Math.min(15, Math.floor(Math.abs(Number(config.count)) || 1)));
    const quality = normalizeImageQuality(config.quality);
    const requestSize = validateModelImageSize(resolveImageRequestSize(config.imageResolution, config.size), modelMaxImageSize(config, config.imageModel || config.model));

    if (config.channelMode === "remote") {
        return requestGenerationAsync(config, prompt, n, quality, requestSize);
    }
    return requestGenerationSync(config, prompt, n, quality, requestSize);
}

/** 异步图片生成：创建任务 → 轮询状态 → 获取结果。绕过 Cloudflare 100s 超时。 */
async function requestGenerationAsync(
    config: AiConfig,
    prompt: string,
    n: number,
    quality: string | undefined,
    requestSize: string | undefined,
) {
    // 1. 创建异步任务
    const task = await createImageTask(config, prompt, n, quality, requestSize);

    // 2. 轮询直到完成（最多 5 分钟，每 2.5 秒一次）
    for (let attempt = 0; attempt < 120; attempt++) {
        const state = await pollImageTask(config, task.id);
        if (state.status === "completed") {
            const images = await fetchImageTaskResult(config, task.id);
            refreshRemoteUser(config);
            return images;
        }
        if (state.status === "failed") {
            throw new Error(state.errorMsg || "图片生成失败");
        }
        await delay(2500);
    }
    throw new Error("图片生成超时，请稍后重试");
}

/** 同步图片生成（直连模式，保持原逻辑）。 */
async function requestGenerationSync(
    config: AiConfig,
    prompt: string,
    n: number,
    quality: string | undefined,
    requestSize: string | undefined,
) {
    try {
        const response = await axios.post<ImageApiResponse>(
            aiApiUrl(config, "/images/generations"),
            {
                model: config.model,
                prompt: withSystemPrompt(config, prompt),
                n,
                ...(quality ? { quality } : {}),
                ...(requestSize ? { size: requestSize } : {}),
                response_format: "b64_json",
                output_format: IMAGE_OUTPUT_FORMAT,
            },
            {
                headers: aiHeaders(config, "application/json"),
            },
        );
        const images = parseImagePayload(response.data);
        refreshRemoteUser(config);
        return images;
    } catch (error) {
        throw new Error(readAxiosError(error, "请求失败"));
    }
}

async function createImageTask(
    config: AiConfig,
    prompt: string,
    n: number,
    quality: string | undefined,
    requestSize: string | undefined,
) {
    try {
        const response = await axios.post<{ code: number; data: { id: string; status: string }; msg: string }>(
            aiApiUrl(config, "/images/generations?async=1"),
            {
                model: config.model,
                prompt: withSystemPrompt(config, prompt),
                n,
                ...(quality ? { quality } : {}),
                ...(requestSize ? { size: requestSize } : {}),
                response_format: "b64_json",
                output_format: IMAGE_OUTPUT_FORMAT,
            },
            { headers: aiHeaders(config, "application/json") },
        );
        const payload = response.data;
        if (payload.code !== 0 || !payload.data?.id) {
            throw new Error(payload.msg || "创建任务失败");
        }
        return { id: payload.data.id };
    } catch (error) {
        throw new Error(readAxiosError(error, "创建图片任务失败"));
    }
}

type ImageTaskState = { status: string; errorMsg?: string };

async function pollImageTask(config: AiConfig, taskId: string): Promise<ImageTaskState> {
    try {
        const response = await axios.get<{ code: number; data: ImageTaskState; msg: string }>(
            aiApiUrl(config, `/images/generations/${encodeURIComponent(taskId)}`),
            { headers: aiHeaders(config) },
        );
        return (response.data as unknown as { data: ImageTaskState }).data;
    } catch (error) {
        throw new Error(readAxiosError(error, "查询图片任务失败"));
    }
}

async function fetchImageTaskResult(config: AiConfig, taskId: string) {
    try {
        const response = await axios.get<ImageApiResponse>(
            aiApiUrl(config, `/images/generations/${encodeURIComponent(taskId)}/result`),
            { headers: aiHeaders(config) },
        );
        return parseImagePayload(response.data);
    } catch (error) {
        throw new Error(readAxiosError(error, "获取图片结果失败"));
    }
}

function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function requestEdit(config: AiConfig, prompt: string, references: ReferenceImage[], mask?: ReferenceImage) {
    const n = Math.max(1, Math.min(15, Math.floor(Math.abs(Number(config.count)) || 1)));
    const quality = normalizeImageQuality(config.quality);
    const requestSize = validateModelImageSize(resolveImageRequestSize(config.imageResolution, config.size), modelMaxImageSize(config, config.imageModel || config.model));
    const requestPrompt = buildImageReferencePromptText(prompt, references);
    const formData = new FormData();
    formData.set("model", config.model);
    formData.set("prompt", withSystemPrompt(config, requestPrompt));
    formData.set("n", String(n));
    formData.set("response_format", "b64_json");
    formData.set("output_format", IMAGE_OUTPUT_FORMAT);
    if (quality) {
        formData.set("quality", quality);
    }
    if (requestSize) {
        formData.set("size", requestSize);
    }
    const files = await Promise.all(references.map(async (image) => dataUrlToFile({ ...image, dataUrl: await imageToDataUrl(image) })));
    files.forEach((file) => formData.append("image", file));
    if (mask) formData.set("mask", dataUrlToFile(mask));

    try {
        const response = await axios.post<ImageApiResponse>(aiApiUrl(config, "/images/edits"), formData, { headers: aiHeaders(config) });
        const images = parseImagePayload(response.data);
        refreshRemoteUser(config);
        return images;
    } catch (error) {
        throw new Error(readAxiosError(error, "请求失败"));
    }
}

export async function requestImageQuestion(config: AiConfig, messages: ChatCompletionMessage[], onDelta: (text: string) => void) {
    let buffer = "";
    let answer = "";
    let processedLength = 0;

    try {
        const response = await axios.post(
            aiApiUrl(config, "/chat/completions"),
            {
                model: config.model,
                messages: withSystemMessage(config, messages),
                stream: true,
            },
            {
                headers: {
                    ...aiHeaders(config, "application/json"),
                } as Record<string, string>,
                responseType: "text",
                onDownloadProgress: (event) => {
                    const responseText = String(event.event?.target?.responseText || "");
                    const nextText = responseText.slice(processedLength);
                    processedLength = responseText.length;
                    buffer += nextText;
                    const chunks = buffer.split("\n\n");
                    buffer = chunks.pop() || "";
                    for (const chunk of chunks) {
                        parseStreamChunk(chunk, (delta) => {
                            answer += delta;
                            onDelta(answer);
                        });
                    }
                },
            },
        );
        if (typeof response.data === "object" && response.data && "code" in response.data && (response.data as { code?: number; msg?: string }).code !== 0) {
            throw new Error((response.data as { msg?: string }).msg || "请求失败");
        }
        if (typeof response.data === "string") {
            let apiError = "";
            try {
                const payload = JSON.parse(response.data) as { code?: number; msg?: string };
                if (typeof payload.code === "number" && payload.code !== 0) {
                    apiError = payload.msg || "请求失败";
                }
            } catch {
                // ignore plain text stream content
            }
            if (apiError) throw new Error(apiError);
        }
        if (buffer) {
            parseStreamChunk(buffer, (delta) => {
                answer += delta;
                onDelta(answer);
            });
        }
    } catch (error) {
        throw new Error(readAxiosError(error, "请求失败"));
    }
    refreshRemoteUser(config);
    return answer || "没有返回内容";
}

export async function fetchImageModels(config: AiConfig) {
    if (config.channelMode === "remote") return config.models;
    try {
        const response = await axios.get<{ data?: Array<{ id?: string }>; error?: { message?: string } }>(buildApiUrl(config.baseUrl, "/models"), {
            headers: {
                Authorization: `Bearer ${config.apiKey}`,
            },
        });
        return (response.data.data || [])
            .map((model) => model.id)
            .filter((id): id is string => Boolean(id))
            .sort((a, b) => a.localeCompare(b));
    } catch (error) {
        throw new Error(readAxiosError(error, "读取模型失败"));
    }
}

function validateModelImageSize(requestSize: string | undefined, maxSize: string) {
    if (!requestSize || !maxSize) return requestSize;
    const maxRank = imageSizeRank(maxSize);
    if (maxRank > 0 && imageSizeRank(requestSize) > maxRank) {
        throw new Error(`当前模型最大支持 ${formatMaxImageSize(maxSize)}，请降低清晰度或调整尺寸`);
    }
    const request = parseImageDimensions(requestSize);
    const max = parseImageDimensions(maxSize);
    if (!request || !max) return requestSize;
    if (request.width > max.width || request.height > max.height) {
        throw new Error(`当前模型最大支持 ${maxSize}，请降低清晰度或调整尺寸`);
    }
    return requestSize;
}

function parseImageDimensions(value: string) {
    const match = value.match(/^(\d+)x(\d+)$/i);
    return match ? { width: Number(match[1]), height: Number(match[2]) } : null;
}

function imageSizeRank(value: string) {
    const normalized = value.trim().toLowerCase();
    if (normalized === "1k") return 1;
    if (normalized === "2k") return 2;
    if (normalized === "4k") return 3;
    const dimensions = parseImageDimensions(normalized);
    if (!dimensions) return 0;
    const edge = Math.max(dimensions.width, dimensions.height);
    if (edge <= 1024) return 1;
    if (edge <= 2048) return 2;
    return 3;
}

function formatMaxImageSize(maxSize: string) {
    return ({ auto: "自动", "1k": "1K", "2k": "2K", "4k": "4K" } as Record<string, string>)[maxSize.trim().toLowerCase()] || maxSize;
}
